import { cn } from "@/lib/utils";

interface Props {
  values: number[];
  /** Logical width for the viewBox coord system. */
  width?: number;
  /** Rendered height in px. Horizontal width fills the parent. */
  height?: number;
  lowBand?: number;
  highBand?: number;
  color?: string;
  className?: string;
}

/** A minimal inline SVG sparkline with optional horizontal threshold bands.
 *  Uses a viewBox so it stretches to fill its container's width. */
export function Sparkline({
  values,
  width = 120,
  height = 28,
  lowBand,
  highBand,
  color = "hsl(var(--muted-foreground))",
  className,
}: Props) {
  if (values.length < 2) {
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className={cn("block h-full w-full", className)}
        style={{ height }}
      />
    );
  }

  const domainLow = Math.min(...values, lowBand ?? Infinity);
  const domainHigh = Math.max(...values, highBand ?? -Infinity);
  const span = domainHigh - domainLow || 1;
  const pad = 1;
  const innerH = height - pad * 2;

  const x = (i: number) => (i / (values.length - 1)) * (width - pad * 2) + pad;
  const y = (v: number) => pad + innerH - ((v - domainLow) / span) * innerH;

  const d = values
    .map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(2)},${y(v).toFixed(2)}`)
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn("block h-full w-full", className)}
      style={{ height }}
    >
      {lowBand !== undefined && (
        <line
          x1={0}
          x2={width}
          y1={y(lowBand)}
          y2={y(lowBand)}
          stroke="hsl(var(--buy))"
          strokeDasharray="2 2"
          opacity={0.4}
        />
      )}
      {highBand !== undefined && (
        <line
          x1={0}
          x2={width}
          y1={y(highBand)}
          y2={y(highBand)}
          stroke="hsl(var(--sell))"
          strokeDasharray="2 2"
          opacity={0.4}
        />
      )}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
